import { useEffect, useState } from 'react';
import { fetchLatestPosts } from '../util/api';

/**
 * Module-level cache, shared by every consumer.
 *
 * The Insights panel mounts in both the desktop mega menu and the mobile
 * drawer, so caching here means the feed is fetched once per page load
 * rather than once per consumer, and a second consumer mounting mid-flight
 * reuses the in-flight promise instead of starting a duplicate request.
 *
 * `null` means "not fetched yet". An empty array is a legitimate cached
 * result (the blog returned nothing) and must be distinguishable from it —
 * `[]` is truthy, so the guard below tests against `null` explicitly.
 */
let cachedPosts = null;
let inFlight = null;

/**
 * Latest blog posts for the header Insights menu.
 *
 * Never throws and never surfaces an error state: the menu has static links
 * that must render regardless, so a blog outage degrades to "no post cards"
 * rather than a broken navigation.
 *
 * NOTE — no AbortController here, deliberately.
 * An earlier version aborted the request in the effect's cleanup, which broke
 * the feature outright under `React.StrictMode`: StrictMode mounts, unmounts
 * and remounts every effect, so the cleanup aborted the shared request before
 * it could finish, the remount attached to the already-rejected promise, and
 * the panel showed no posts for the rest of the page load. Aborting a shared,
 * cached, single request buys nothing — the only thing an unmounted consumer
 * needs to do is stop calling setState.
 *
 * @returns {{ posts: Array, isLoading: boolean }}
 */
export const useLatestPosts = () => {
  const [posts, setPosts] = useState(cachedPosts || []);
  const [isLoading, setIsLoading] = useState(cachedPosts === null);

  useEffect(() => {
    if (cachedPosts !== null) return undefined;

    let active = true;

    if (!inFlight) {
      inFlight = fetchLatestPosts()
        .then(data => {
          const list = Array.isArray(data?.posts) ? data.posts : [];
          // Cache only a successful response.
          cachedPosts = list;
          return list;
        })
        .catch(() => {
          // Leave the cache untouched so a later mount can retry. Caching a
          // failure would pin the menu to "no posts" for the whole session.
          return [];
        })
        .finally(() => {
          inFlight = null;
        });
    }

    inFlight.then(result => {
      if (!active) return;
      setPosts(result);
      setIsLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  return { posts, isLoading };
};

export default useLatestPosts;
